import iconsPng from '@/assets/img/png/icons';
import ModalContainer from '@/components/molecules/ModalContainer';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import { Center, Image, Input, Text, VStack } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MIN_CHARS_IN_BIO, MAX_CHARS_IN_BIO } from '@/constants';
import CustomButton from '@/components/atoms/CustomButton';
import useUpdateNewPeerSupportProfile from '@/hooks/peersupport/updateNewPeerSupportProfile';
import { TUserMetadata } from '@/types/User.types';
import { TPeerSupportProfileInfo } from '@/types/PeerSupport.types';
import { useAuthContext } from '@/context/auth/useAuthContext';

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: (_: boolean) => void;
  userMetadata: TUserMetadata;
}

const peerSupportBioSchema = yup
  .object()
  .shape({
    bio: yup
      .string()
      .required('Bio is required.')
      .min(MIN_CHARS_IN_BIO, `Minimum ${MIN_CHARS_IN_BIO} characters required in Bio`)
      .max(MAX_CHARS_IN_BIO, `Maximum ${MAX_CHARS_IN_BIO} characters allowed in Bio`),
  })
  .required();

const UserPeerSupportBioModal = ({ isModalOpen, setIsModalOpen, userMetadata }: Props) => {
  const isFirstBioUpdate = !userMetadata.bio;

  const { refreshUserData } = useAuthContext();

  const {
    register,
    getValues,
    setValue,
    reset: resetBioForm,
    formState,
    watch,
  } = useForm<{ bio: string }>({
    resolver: yupResolver(peerSupportBioSchema),
    mode: 'all',
    defaultValues: {
      bio: userMetadata.bio,
    },
  });

  useEffect(() => {
    setValue('bio', userMetadata.bio ?? '');
  }, [userMetadata.bio]);

  const currentBioValue = watch('bio');

  const { isValid, errors, touchedFields } = formState;

  const { mutate: updateNewPeerSupportBioMutation, isPending } = useUpdateNewPeerSupportProfile(() => {
    resetBioForm();
    setIsModalOpen(false);

    refreshUserData();
  });

  const updatePeerSupportBio = () => {
    const vals = getValues();

    const peerSupportUpdateInfo: TPeerSupportProfileInfo = {
      bio: vals.bio,
      firstName: userMetadata.firstName,
      lastName: userMetadata.lastName,
      phone: userMetadata.phone,
      calendly_link: userMetadata.calendly_link,
      dateOfBirth: userMetadata.dateOfBirth,
      profilePicture: userMetadata.profilePicture,
    };
    updateNewPeerSupportBioMutation({ peerSupportUpdateInfo });
  };

  return (
    <ModalContainer
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      isBlur={isFirstBioUpdate}
      width={'40%'}
      height={'20vh'}
      backgroundColor={'background.lightBlue'}
      closeOnOverlayClick={!isFirstBioUpdate}
      closeButtonHidden={isFirstBioUpdate}
    >
      <Text variant={'loraTitle'} textAlign={'center'} fontSize={'26px'} marginTop={'20px'} whiteSpace={'pre-wrap'}>
        {isFirstBioUpdate ? 'Set up your Peer Support Profile' : 'Update Your Bio'}
      </Text>
      {isFirstBioUpdate && (
        <>
          <Center marginTop={'20px'}>
            <Image alt="add-member" src={iconsPng.handshake} width={120} height={120} />
          </Center>
          <VStack marginTop={'40px'}>
            <Text variant={'urbanistSemiBoldLarge'} color={'text.mediumBlue'} maxWidth={'300px'}>
              Congrats on your heroic endeavour! Your account has been upgraded to Peer Support. Add a short bio that
              your team will see.
            </Text>
          </VStack>
        </>
      )}
      <Center>
        <VStack marginY={'40px'} width={FORM_FIELD_WIDTH} gap={'20px'}>
          <Input
            {...register('bio', { required: true })}
            as={'textarea'}
            className="custom-input"
            placeholder={'Bio'}
            _placeholder={{
              fontWeight: '600',
              color: 'background.blueGray',
              fontSize: '14px',
            }}
            minHeight={'150px'}
            maxHeight={'350px'}
            data-state={touchedFields.bio ? (errors.bio?.message ? 'invalid' : 'valid') : ''}
          />

          {errors.bio && <Text variant={'error'}>{errors.bio.message}</Text>}
          <CustomButton
            style={{ marginTop: '20px' }}
            backgroundColor="secondary.500"
            label={isFirstBioUpdate ? 'Confirm' : 'Update'}
            width={FORM_FIELD_WIDTH}
            labelColor="white"
            onClick={updatePeerSupportBio}
            disabled={!isValid || currentBioValue === userMetadata.bio}
            isLoading={isPending}
          />
        </VStack>
      </Center>
    </ModalContainer>
  );
};

export default UserPeerSupportBioModal;
